import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DsAlert,
  DsBadge,
  DsButton,
  DsEmptyState,
  DsInput,
  DsPageHeader,
  DsSpinner,
  PAGE_LAYOUT,
} from "../../../design-system";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { notificationSettingsService } from "../../../services/api";
import HiringPortalProvider from "../hiring/HiringPortalProvider";
import SettingsPage from "../hiring/pages/SettingsPage";
import portalApi from "../hiring/api";
import { NOTIFICATION_EVENTS } from "../hiring/constants/notificationEvents";
import {
  EMAIL_TEMPLATE_DEFAULTS,
  resolveTemplateContent,
} from "../hiring/constants/emailTemplateDefaults";

const PORTAL_TABS = {
  "portal-smtp": "email",
  "portal-notifs": "notifications",
};

const EMPTY_HTML = `<p style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#3f3f46;line-height:1.5;">
  Hello {{employeeName}},<br/><br/>
  Message from <strong>{{companyName}}</strong>.<br/><br/>
  — PaceHRM
</p>`;

/** Switch that avoids global outline-button CSS (solid primary buttons → hollow outline). */
function EnabledSwitch({
  checked,
  disabled,
  onClick,
  title,
  showLabel = true,
}) {
  const on = !!checked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className="inline-flex items-center gap-2 border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        aria-hidden
        className="relative block shrink-0"
        style={{
          width: 44,
          height: 24,
          minWidth: 44,
          minHeight: 24,
          boxSizing: "border-box",
          borderRadius: 9999,
          border: on
            ? "2px solid hsl(var(--ds-primary))"
            : "2px solid hsl(var(--ds-border))",
          backgroundColor: on
            ? "hsl(var(--ds-primary))"
            : "hsl(var(--ds-muted))",
          boxShadow: "inset 0 0 0 1px hsl(var(--ds-border) / 0.35)",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: on ? 22 : 2,
            width: 16,
            height: 16,
            borderRadius: 9999,
            backgroundColor: "#fff",
            border: "1px solid hsl(var(--ds-border))",
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
            transition: "left 120ms ease",
          }}
        />
      </span>
      {showLabel ? (
        <span
          className={`ds-caption font-semibold ${
            on
              ? "text-[hsl(var(--ds-primary))]"
              : "text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          {on ? "On" : "Off"}
        </span>
      ) : null}
    </button>
  );
}

function buildHiringPortalRows(portalSettings) {
  const rules = portalSettings?.notificationRules || {};
  const templates = portalSettings?.emailTemplates || {};
  return NOTIFICATION_EVENTS.map((ev) => {
    const def = EMAIL_TEMPLATE_DEFAULTS[ev.id] || {};
    const resolved = resolveTemplateContent(ev.id, templates[ev.id], {
      eventType: ev.id,
      defaultSubject: def.subject || "",
      defaultBodyText: def.bodyText || "",
      buttonLabel: def.buttonLabel,
      buttonPath: def.buttonPath,
    });
    const rule = rules[ev.id] || {};
    return {
      kind: "hiring_portal",
      triggerKey: `portal_${ev.id}`,
      eventType: ev.id,
      name: ev.label,
      moduleLabel: "Hiring portal",
      groupLabel: ev.group,
      mandatory: false,
      enabled: rule.email !== false,
      audienceMode: "portal",
      audienceLabel: ev.group,
      subjectTemplate: resolved.subject,
      bodyText: resolved.bodyText,
      buttonLabel: resolved.buttonLabel || def.buttonLabel,
      buttonPath: resolved.buttonPath || def.buttonPath,
    };
  });
}

export default function NotificationEmailsSettings() {
  const navigate = useNavigate();
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canView =
    isAdmin || hasScreenAccess("workspaces", "notification_emails", "view");
  const canEdit =
    isAdmin || hasScreenAccess("workspaces", "notification_emails", "edit");

  const [tab, setTab] = useState("product");
  // Option A: tabs under My custom alerts — revert by removing customSubTab + always showing both panels stacked
  const [customSubTab, setCustomSubTab] = useState("alerts"); // "alerts" | "log"
  const [product, setProduct] = useState([]);
  const [hiringPortalRows, setHiringPortalRows] = useState([]);
  const [portalSettings, setPortalSettings] = useState(null);
  const [custom, setCustom] = useState([]);
  const [catalog, setCatalog] = useState({ customEvents: [], tokensHelp: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [editing, setEditing] = useState(null); // { kind, ...row }
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [draft, setDraft] = useState({
    name: "",
    enabled: true,
    audienceMode: "role",
    audienceRoleCode: "",
    subjectTemplate: "",
    htmlTemplate: "",
    bodyText: "",
    whenType: "event",
    whenEventKey: "payroll_finalised",
    scheduleCron: "",
  });

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError("");
    try {
      const [listRes, catRes, portalRes] = await Promise.all([
        notificationSettingsService.list(),
        notificationSettingsService.getCatalog(),
        portalApi.get("/settings").catch(() => null),
      ]);
      const data = listRes?.data || listRes;
      setProduct(data?.product || []);
      setCustom(data?.custom || []);
      setCatalog(catRes?.data || catRes || { customEvents: [], tokensHelp: [] });
      const portal = portalRes?.data || null;
      setPortalSettings(portal);
      setHiringPortalRows(buildHiringPortalRows(portal));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  const productTableRows = useMemo(
    () => [...product, ...hiringPortalRows],
    [product, hiringPortalRows]
  );

  const openEdit = (row, kind) => {
    const resolvedKind = kind || row.kind || "product";
    setEditing({ ...row, kind: resolvedKind });
    setDraft({
      name: row.name || "",
      enabled: row.enabled !== false,
      audienceMode: row.audienceMode || "employee",
      audienceRoleCode: row.audienceRoleCode || "",
      subjectTemplate: row.subjectTemplate || "",
      htmlTemplate: row.htmlTemplate || EMPTY_HTML,
      bodyText: row.bodyText || "",
      whenType: row.whenType || "event",
      whenEventKey: row.whenEventKey || "payroll_finalised",
      scheduleCron: row.scheduleCron || "",
    });
  };

  const saveEdit = async () => {
    if (!canEdit || !editing) return;
    setSaving(true);
    setError("");
    try {
      if (editing.kind === "hiring_portal") {
        const eventType = editing.eventType;
        const nextTemplates = {
          ...(portalSettings?.emailTemplates || {}),
          [eventType]: {
            subject: draft.subjectTemplate,
            bodyText: draft.bodyText,
          },
        };
        const prevRule = portalSettings?.notificationRules?.[eventType] || {};
        const nextRules = {
          ...(portalSettings?.notificationRules || {}),
          [eventType]: { ...prevRule, email: draft.enabled !== false },
        };
        await portalApi.put("/settings", {
          emailTemplates: nextTemplates,
          notificationRules: nextRules,
        });
      } else if (editing.kind === "product") {
        await notificationSettingsService.saveProduct(editing.triggerKey, {
          enabled: editing.mandatory ? true : draft.enabled,
          audienceMode: draft.audienceMode,
          audienceRoleCode: draft.audienceRoleCode || null,
          subjectTemplate: draft.subjectTemplate,
          htmlTemplate: draft.htmlTemplate,
        });
      } else {
        await notificationSettingsService.updateCustom(editing.id, {
          name: draft.name,
          enabled: draft.enabled,
          audienceMode: draft.audienceMode,
          audienceRoleCode: draft.audienceRoleCode || null,
          subjectTemplate: draft.subjectTemplate,
          htmlTemplate: draft.htmlTemplate,
          whenType: draft.whenType,
          whenEventKey: draft.whenEventKey,
          scheduleCron: draft.scheduleCron || null,
        });
      }
      setToast("Saved");
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleProduct = async (row) => {
    if (!canEdit || row.mandatory) return;
    try {
      if (row.kind === "hiring_portal") {
        const eventType = row.eventType;
        const prevRule = portalSettings?.notificationRules?.[eventType] || {};
        await portalApi.put("/settings", {
          notificationRules: {
            ...(portalSettings?.notificationRules || {}),
            [eventType]: { ...prevRule, email: !row.enabled },
          },
        });
      } else {
        await notificationSettingsService.saveProduct(row.triggerKey, {
          enabled: !row.enabled,
          subjectTemplate: row.subjectTemplate,
          htmlTemplate: row.htmlTemplate,
          audienceMode: row.audienceMode,
          audienceRoleCode: row.audienceRoleCode,
        });
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const createAlert = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await notificationSettingsService.createCustom({
        name: draft.name || "Untitled alert",
        enabled: true,
        audienceMode: draft.audienceMode,
        audienceRoleCode: draft.audienceRoleCode || null,
        subjectTemplate:
          draft.subjectTemplate || "[{{companyName}}] Alert: {{eventName}}",
        htmlTemplate: draft.htmlTemplate || EMPTY_HTML,
        whenType: draft.whenType,
        whenEventKey: draft.whenEventKey,
        scheduleCron: draft.scheduleCron || null,
      });
      setCreateOpen(false);
      setTab("custom");
      setToast("Custom alert created");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeCustom = async (row) => {
    if (!canEdit) return;
    if (!window.confirm(`Delete custom alert “${row.name}”?`)) return;
    try {
      await notificationSettingsService.removeCustom(row.id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const sendTest = async () => {
    if (!canEdit) return;
    try {
      const res = await notificationSettingsService.sendTest({
        triggerKey: editing?.triggerKey || "test",
        subjectTemplate: draft.subjectTemplate,
        htmlTemplate: draft.htmlTemplate,
      });
      setToast(res?.message || "Test email sent");
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const enabledCount = useMemo(
    () =>
      product.filter((p) => p.enabled).length +
      hiringPortalRows.filter((p) => p.enabled).length +
      custom.filter((c) => c.enabled).length,
    [product, hiringPortalRows, custom]
  );

  const isHiringPortalEdit = editing?.kind === "hiring_portal";

  if (!canView) {
    return (
      <div className={PAGE_LAYOUT.page}>
        <div className={PAGE_LAYOUT.shell}>
          <div className={PAGE_LAYOUT.content}>
            <DsEmptyState
              title="Access denied"
              description="Enable Notification emails in User Role Management → Screen Access."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={PAGE_LAYOUT.page}>
      <div className={PAGE_LAYOUT.shell}>
        {error ? (
          <div className={PAGE_LAYOUT.alert}>
            <DsAlert tone="danger">{error}</DsAlert>
          </div>
        ) : null}
        {toast ? (
          <div className={PAGE_LAYOUT.alert}>
            <DsAlert tone="success">{toast}</DsAlert>
          </div>
        ) : null}

        <div className={PAGE_LAYOUT.body}>
          <aside className={PAGE_LAYOUT.aside}>
            <div className={PAGE_LAYOUT.asideHead}>
              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Settings</p>
              <h2 className="ds-h4">Notifications</h2>
            </div>
            <div className={PAGE_LAYOUT.asideNav}>
              <button
                type="button"
                className={
                  tab === "product" && !editing && !createOpen
                    ? PAGE_LAYOUT.navItemActive
                    : PAGE_LAYOUT.navItem
                }
                onClick={() => {
                  setTab("product");
                  setEditing(null);
                  setCreateOpen(false);
                }}
              >
                Product notifications
              </button>
              <button
                type="button"
                className={
                  tab === "custom" && !editing && !createOpen
                    ? PAGE_LAYOUT.navItemActive
                    : PAGE_LAYOUT.navItem
                }
                onClick={() => {
                  setTab("custom");
                  setCustomSubTab("alerts");
                  setEditing(null);
                  setCreateOpen(false);
                }}
              >
                My custom alerts
              </button>
              <button
                type="button"
                className={
                  tab === "portal-smtp" ? PAGE_LAYOUT.navItemActive : PAGE_LAYOUT.navItem
                }
                onClick={() => {
                  setTab("portal-smtp");
                  setEditing(null);
                  setCreateOpen(false);
                }}
              >
                Email / SMTP
              </button>
              <button
                type="button"
                className={
                  tab === "portal-notifs"
                    ? PAGE_LAYOUT.navItemActive
                    : PAGE_LAYOUT.navItem
                }
                onClick={() => {
                  setTab("portal-notifs");
                  setEditing(null);
                  setCreateOpen(false);
                }}
              >
                Hiring portal channels
              </button>
              <button
                type="button"
                className={PAGE_LAYOUT.navItem}
                onClick={() => navigate("/admin/workspace/settings")}
              >
                Back to Settings
              </button>
            </div>
            <div className={PAGE_LAYOUT.asideFoot}>
              <p className="ds-small text-[hsl(var(--ds-muted-fg))]">
                {product.length} product · {hiringPortalRows.length} hiring ·{" "}
                {custom.length} custom · {enabledCount} enabled
              </p>
            </div>
          </aside>

          <div className={PAGE_LAYOUT.main}>
            <div className={PAGE_LAYOUT.content}>
              {!editing && !createOpen && PORTAL_TABS[tab] ? (
                <>
                  <div className="mb-4">
                    <DsPageHeader
                      title={
                        tab === "portal-smtp"
                          ? "Email / SMTP"
                          : "Hiring portal channels"
                      }
                      description={
                        tab === "portal-smtp"
                          ? "Configure hiring portal SMTP and send a test message."
                          : "Choose push, email, and WhatsApp channels for each recruitment event."
                      }
                      infoTooltip="Email / SMTP configures hiring portal mail delivery. Hiring portal channels control push, email, and WhatsApp per recruitment event. Hiring email templates appear as Hiring portal rows under Product notifications. The hiring sent-email log is under My custom alerts → Hiring email log."
                    />
                  </div>
                  <div className={PAGE_LAYOUT.panel}>
                    <HiringPortalProvider>
                      <SettingsPage
                        embedded
                        hideTabBar
                        visibleTabs={[PORTAL_TABS[tab]]}
                        initialTab={PORTAL_TABS[tab]}
                      />
                    </HiringPortalProvider>
                  </div>
                </>
              ) : null}

              {!editing && !createOpen && (tab === "product" || tab === "custom") ? (
                <>
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <DsPageHeader
                      title={
                        tab === "custom"
                          ? customSubTab === "log"
                            ? "Hiring email log"
                            : "My custom alerts"
                          : "Notification emails"
                      }
                      description={
                        tab === "custom"
                          ? customSubTab === "log"
                            ? "Sent recruitment emails, recipients, status, and delivery errors."
                            : "Create and manage company-scoped custom alerts for product events."
                          : "Enable triggers, choose audience, edit subject and professional HTML. Delivery is logged in Activity Log Monitor."
                      }
                      infoTooltip={
                        tab === "custom"
                          ? "My alerts configures future sends. Hiring email log is a read-only history of hiring portal deliveries — search and preview only; it is not the same as creating an alert."
                          : "Product notifications include payroll/HR triggers and Hiring portal email templates (same Edit template flow; portal rows save to the hiring portal API). Push/WhatsApp channel matrix is under Hiring portal channels. SMTP is under Email / SMTP."
                      }
                    />
                    {canEdit && tab === "custom" && customSubTab === "alerts" ? (
                      <DsButton
                        variant="primary"
                        onClick={() => {
                          setDraft({
                            name: "",
                            enabled: true,
                            audienceMode: "role",
                            audienceRoleCode: "",
                            subjectTemplate:
                              "[{{companyName}}] Alert: {{eventName}}",
                            htmlTemplate: EMPTY_HTML,
                            whenType: "event",
                            whenEventKey:
                              catalog.customEvents?.[0]?.key ||
                              "payroll_finalised",
                            scheduleCron: "",
                          });
                          setCreateOpen(true);
                        }}
                      >
                        Create alert
                      </DsButton>
                    ) : null}
                  </div>

                  {tab === "custom" ? (
                    <div className="mb-4 flex flex-wrap gap-2 border-b border-[hsl(var(--ds-border))] pb-2">
                      {[
                        { id: "alerts", label: "My alerts" },
                        { id: "log", label: "Hiring email log" },
                      ].map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className={
                            customSubTab === t.id
                              ? "rounded-xl border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] px-4 py-2 text-sm font-semibold text-[hsl(var(--ds-primary))] shadow-sm"
                              : "rounded-xl border border-transparent px-4 py-2 text-sm font-medium text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                          }
                          onClick={() => setCustomSubTab(t.id)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {loading ? (
                    <div className="flex justify-center py-16">
                      <DsSpinner />
                    </div>
                  ) : tab === "product" ? (
                    <div className={PAGE_LAYOUT.panel}>
                      <div className="overflow-auto">
                        <table className="ds-table w-full text-left text-sm">
                          <thead>
                            <tr>
                              <th>Notification</th>
                              <th>Module</th>
                              <th>Enabled</th>
                              <th>Send to</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {productTableRows.map((row) => (
                              <tr key={row.triggerKey}>
                                <td>
                                  {row.name}{" "}
                                  {row.mandatory ? (
                                    <DsBadge tone="warning">Mandatory</DsBadge>
                                  ) : null}
                                  {row.kind === "hiring_portal" ? (
                                    <DsBadge tone="info">Portal</DsBadge>
                                  ) : null}
                                </td>
                                <td>{row.moduleLabel}</td>
                                <td className="whitespace-nowrap">
                                  <EnabledSwitch
                                    checked={row.enabled}
                                    disabled={!canEdit || row.mandatory}
                                    title={
                                      row.mandatory
                                        ? "Mandatory — always on"
                                        : row.enabled
                                          ? "On — click to turn off"
                                          : "Off — click to turn on"
                                    }
                                    onClick={() => toggleProduct(row)}
                                  />
                                </td>
                                <td>
                                  {row.kind === "hiring_portal"
                                    ? row.audienceLabel || row.groupLabel || "Portal"
                                    : row.audienceMode === "role"
                                      ? `Role${
                                          row.audienceRoleCode
                                            ? `: ${row.audienceRoleCode}`
                                            : ""
                                        }`
                                      : "Employee"}
                                </td>
                                <td>
                                  {canEdit ? (
                                    <button
                                      type="button"
                                      className="text-[hsl(var(--ds-primary))] font-semibold text-xs"
                                      onClick={() =>
                                        openEdit(
                                          row,
                                          row.kind === "hiring_portal"
                                            ? "hiring_portal"
                                            : "product"
                                        )
                                      }
                                    >
                                      Edit template
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : customSubTab === "alerts" ? (
                    <div className={PAGE_LAYOUT.panel}>
                      {custom.length === 0 ? (
                        <DsEmptyState
                          title="No custom alerts"
                          description="Create an alert for a product event, schedule, or one-time send."
                        />
                      ) : (
                        <div className="overflow-auto">
                          <table className="ds-table w-full text-left text-sm">
                            <thead>
                              <tr>
                                <th>Alert name</th>
                                <th>When</th>
                                <th>Enabled</th>
                                <th>Send to</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {custom.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    {row.name}{" "}
                                    <DsBadge tone="info">Custom</DsBadge>
                                  </td>
                                  <td>{row.triggerLabel}</td>
                                  <td>
                                    {row.enabled ? (
                                      <DsBadge tone="success">On</DsBadge>
                                    ) : (
                                      <DsBadge tone="neutral">Off</DsBadge>
                                    )}
                                  </td>
                                  <td>
                                    {row.audienceMode}
                                    {row.audienceRoleCode
                                      ? `: ${row.audienceRoleCode}`
                                      : ""}
                                  </td>
                                  <td className="space-x-3">
                                    {canEdit ? (
                                      <>
                                        <button
                                          type="button"
                                          className="text-[hsl(var(--ds-primary))] font-semibold text-xs"
                                          onClick={() =>
                                            openEdit(row, "custom")
                                          }
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="text-[hsl(var(--ds-danger))] font-semibold text-xs"
                                          onClick={() => removeCustom(row)}
                                        >
                                          Delete
                                        </button>
                                      </>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={PAGE_LAYOUT.panel}>
                      <HiringPortalProvider>
                        <SettingsPage
                          embedded
                          hideTabBar
                          visibleTabs={["emailAlerts"]}
                          initialTab="emailAlerts"
                        />
                      </HiringPortalProvider>
                    </div>
                  )}
                </>
              ) : null}

              {(editing || createOpen) && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <DsPageHeader
                      title={
                        createOpen
                          ? "Create custom alert"
                          : `Edit: ${editing?.name || ""}`
                      }
                      description={
                        isHiringPortalEdit
                          ? `Hiring portal · ${editing?.groupLabel || "recruitment"} — plain text; branding and CTA added on send`
                          : editing?.kind === "product"
                            ? `Module · ${editing?.moduleLabel || ""}`
                            : "Custom alert · company scoped"
                      }
                      infoTooltip={
                        isHiringPortalEdit
                          ? "This template is stored in hiring portal settings (subject + plain body). Enable toggles the portal email channel for this event. Push/WhatsApp stay under Hiring portal channels."
                          : undefined
                      }
                    />
                    <DsButton
                      variant="ghost"
                      onClick={() => {
                        setEditing(null);
                        setCreateOpen(false);
                      }}
                    >
                      Back
                    </DsButton>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className={PAGE_LAYOUT.panel}>
                      <div className={PAGE_LAYOUT.panelHead}>Delivery rules</div>
                      <div className="space-y-3 p-4">
                        {(createOpen || editing?.kind === "custom") && (
                          <label className="block">
                            <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                              Alert name
                            </span>
                            <DsInput
                              value={draft.name}
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, name: e.target.value }))
                              }
                              disabled={!canEdit}
                            />
                          </label>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="ds-body">
                            {isHiringPortalEdit ? "Email channel" : "Enable"}
                          </span>
                          <EnabledSwitch
                            checked={draft.enabled}
                            disabled={
                              !canEdit ||
                              (editing?.kind === "product" && editing?.mandatory)
                            }
                            title={
                              draft.enabled
                                ? "On — click to turn off"
                                : "Off — click to turn on"
                            }
                            onClick={() =>
                              setDraft((d) => ({ ...d, enabled: !d.enabled }))
                            }
                          />
                        </div>
                        {(createOpen || editing?.kind === "custom") && (
                          <>
                            <label className="block">
                              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                                When
                              </span>
                              <select
                                className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] px-3 text-sm"
                                value={draft.whenType}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    whenType: e.target.value,
                                  }))
                                }
                              >
                                <option value="event">On product event</option>
                                <option value="schedule">On a schedule</option>
                                <option value="once">One-time / send now</option>
                              </select>
                            </label>
                            {draft.whenType === "event" ? (
                              <label className="block">
                                <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                                  Event
                                </span>
                                <select
                                  className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] px-3 text-sm"
                                  value={draft.whenEventKey}
                                  onChange={(e) =>
                                    setDraft((d) => ({
                                      ...d,
                                      whenEventKey: e.target.value,
                                    }))
                                  }
                                >
                                  {(catalog.customEvents || []).map((ev) => (
                                    <option key={ev.key} value={ev.key}>
                                      {ev.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            ) : null}
                          </>
                        )}
                        <label className="block">
                          <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                            Subject line
                          </span>
                          <DsInput
                            value={draft.subjectTemplate}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                subjectTemplate: e.target.value,
                              }))
                            }
                            disabled={!canEdit}
                          />
                        </label>
                        {isHiringPortalEdit ? (
                          <p className="ds-small text-[hsl(var(--ds-muted-fg))]">
                            Audience: {editing?.groupLabel || "portal recipients"}{" "}
                            (fixed by event). CTA:{" "}
                            {editing?.buttonLabel || "Open portal"}
                            {editing?.buttonPath
                              ? ` → ${editing.buttonPath}`
                              : ""}
                          </p>
                        ) : (
                          <>
                            <label className="block">
                              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                                Send to
                              </span>
                              <select
                                className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] px-3 text-sm"
                                value={draft.audienceMode}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    audienceMode: e.target.value,
                                  }))
                                }
                                disabled={!canEdit}
                              >
                                <option value="employee">
                                  Specific employee (subject of event)
                                </option>
                                <option value="role">Role</option>
                                <option value="both">Employee + role (CC)</option>
                              </select>
                            </label>
                            {draft.audienceMode !== "employee" ? (
                              <label className="block">
                                <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                                  Role code
                                </span>
                                <DsInput
                                  value={draft.audienceRoleCode}
                                  onChange={(e) =>
                                    setDraft((d) => ({
                                      ...d,
                                      audienceRoleCode: e.target.value,
                                    }))
                                  }
                                  placeholder="e.g. HR_ADMIN"
                                  disabled={!canEdit}
                                />
                              </label>
                            ) : null}
                          </>
                        )}
                        <p className="ds-small text-[hsl(var(--ds-muted-fg))]">
                          Tokens:{" "}
                          {isHiringPortalEdit
                            ? "{{companyName}}, {{recipientName}}, {{candidateName}}, {{jobTitle}}, {{loginUrl}}, …"
                            : (catalog.tokensHelp || [])
                                .map((t) => `{{${t}}}`)
                                .join(", ")}
                        </p>
                      </div>
                    </div>

                    <div className={PAGE_LAYOUT.panel}>
                      <div className={PAGE_LAYOUT.panelHead}>Live preview</div>
                      <div className="p-4">
                        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4">
                          <div className="mx-auto max-w-md overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
                            <div className="border-b border-[hsl(var(--ds-border))] px-4 py-3">
                              <p className="ds-body font-semibold">PaceHRM</p>
                              <p className="ds-small text-[hsl(var(--ds-muted-fg))]">
                                {draft.subjectTemplate || "Subject"}
                              </p>
                            </div>
                            {isHiringPortalEdit ? (
                              <pre className="whitespace-pre-wrap px-4 py-4 text-sm font-sans text-[hsl(var(--ds-fg))]">
                                {(draft.bodyText || "")
                                  .replace(/\{\{\s*recipientName\s*\}\}/g, "Priya Sharma")
                                  .replace(/\{\{\s*candidateName\s*\}\}/g, "Priya Sharma")
                                  .replace(/\{\{\s*companyName\s*\}\}/g, "Tutorcloud Private Limited")
                                  .replace(/\{\{\s*roleLabel\s*\}\}/g, "Candidate")
                                  .replace(/\{\{\s*loginEmail\s*\}\}/g, "priya@example.com")
                                  .replace(/\{\{\s*password\s*\}\}/g, "••••••••")
                                  .replace(/\{\{\s*jobTitle\s*\}\}/g, "Software Engineer")
                                  .replace(/\{\{\s*loginUrl\s*\}\}/g, "https://portal.example.com/login") ||
                                  "Preview"}
                              </pre>
                            ) : (
                              <div
                                className="px-4 py-4 text-sm"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    (draft.htmlTemplate || "")
                                      .replace(/\{\{\s*employeeName\s*\}\}/g, "Priya Sharma")
                                      .replace(/\{\{\s*companyName\s*\}\}/g, "Tutorcloud Private Limited")
                                      .replace(/\{\{\s*email\s*\}\}/g, "priya@example.com")
                                      .replace(/\{\{\s*tempPassword\s*\}\}/g, "••••••••")
                                      .replace(/\{\{\s*eventName\s*\}\}/g, "Sample event")
                                      .replace(/\{\{\s*payMonth\s*\}\}/g, "May-2026") ||
                                    "<p class='ds-body'>Preview</p>",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={PAGE_LAYOUT.panel}>
                    <div className={PAGE_LAYOUT.panelHead}>
                      {isHiringPortalEdit ? "Message (plain text)" : "HTML template"}
                    </div>
                    <div className="space-y-3 p-4">
                      <textarea
                        className="min-h-[220px] w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 font-mono text-xs"
                        value={
                          isHiringPortalEdit
                            ? draft.bodyText
                            : draft.htmlTemplate
                        }
                        onChange={(e) =>
                          setDraft((d) =>
                            isHiringPortalEdit
                              ? { ...d, bodyText: e.target.value }
                              : { ...d, htmlTemplate: e.target.value }
                          )
                        }
                        disabled={!canEdit}
                      />
                      <div className="flex flex-wrap gap-2">
                        {canEdit ? (
                          <DsButton
                            variant="primary"
                            disabled={saving}
                            onClick={createOpen ? createAlert : saveEdit}
                          >
                            {saving
                              ? "Saving…"
                              : createOpen
                                ? "Save alert"
                                : "Save template"}
                          </DsButton>
                        ) : null}
                        {canEdit && !createOpen && !isHiringPortalEdit ? (
                          <DsButton variant="secondary" onClick={sendTest}>
                            Send test email
                          </DsButton>
                        ) : null}
                        <DsButton
                          variant="ghost"
                          onClick={() => {
                            if (isHiringPortalEdit) {
                              const def =
                                EMAIL_TEMPLATE_DEFAULTS[editing?.eventType] || {};
                              setDraft((d) => ({
                                ...d,
                                subjectTemplate: def.subject || d.subjectTemplate,
                                bodyText: def.bodyText || "",
                              }));
                              return;
                            }
                            setDraft((d) => ({
                              ...d,
                              htmlTemplate: EMPTY_HTML,
                            }));
                          }}
                        >
                          {isHiringPortalEdit
                            ? "Reset to default text"
                            : "Reset sample HTML"}
                        </DsButton>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
